/**
 * PaywallModal — když API vrátí 403 ``plan does not include`` nebo
 * 422 ``plan_count_limit_reached``, FE komponenta zobrazí upgrade CTA.
 *
 * aurora01 (PR-0): součást NOGO compliance — žádné hardcoded UI texty
 * "tento tier neumí X". Místo toho jeden modal který čte detail z
 * API response (entitlement key / resource / current / limit) a pošle
 * uživatele na stránku plánu tenanta (admin) nebo na veřejný ceník.
 *
 * Použití:
 *   const [paywall, setPaywall] = useState<PaywallContext | null>(null);
 *   try { ... } catch (e) {
 *     if (e instanceof ApiError) {
 *       const ctx = paywallFromError(e);
 *       if (ctx) setPaywall(ctx);
 *     }
 *   }
 *   {paywall && <PaywallModal context={paywall} onClose={() => setPaywall(null)} />}
 */

import { useTranslation } from "react-i18next";

import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { IngotModal } from "@/ingot";
import { ApiError } from "@/lib/api";
import { planLimitResourceLabel } from "@/lib/planLimitMessage";

export interface PaywallContext {
  /** Either "entitlement" (403) or "count_limit" (422). */
  kind: "entitlement" | "count_limit";
  /** For "entitlement": the missing key (e.g. "sso.saml"). */
  entitlementKey?: string;
  /** For "count_limit": the resource that hit the cap (e.g. "materials"). */
  resource?: string;
  /** Current usage count, when known. */
  current?: number;
  /** Plan cap, when known. */
  limit?: number;
  /** Plan tier the org is currently on, when known. */
  planTier?: string;
}

/**
 * Try to derive a paywall context from an :class:`ApiError`. Returns
 * null when the error isn't a paywall-shaped response.
 */
export function paywallFromError(err: ApiError): PaywallContext | null {
  if (err.status === 422 && err.code === "plan_count_limit_reached") {
    const params = err.params as {
      resource?: string;
      current?: number;
      limit?: number;
      plan_tier?: string;
      plan_name?: string;
    };
    return {
      kind: "count_limit",
      resource: params.resource,
      current: params.current,
      limit: params.limit,
      planTier: params.plan_name ?? params.plan_tier,
    };
  }
  if (err.status === 403) {
    // Entitlement gate raises HTTPException with detail = "Plan does
    // not include: <key>" + header X-Entitlement-Required = key. The
    // client wrapper doesn't surface response headers, so parse the
    // detail message as the source of truth.
    const msg = err.message ?? "";
    const match = msg.match(/Plan does not include:\s*(\S+)/i);
    if (match) {
      return { kind: "entitlement", entitlementKey: match[1] };
    }
  }
  return null;
}

function langOf(pathname: string, fallbackLang: string): string {
  const segments = pathname.split("/").filter(Boolean);
  return isSupportedLang(segments[0])
    ? segments[0]
    : isSupportedLang(fallbackLang)
      ? fallbackLang
      : DEFAULT_LANG;
}

/**
 * Is the active route the tenant's own admin shell (``/{lang}/admin/*``)?
 *
 * KAN-1494 — this decides WHAT the modal says, not just where its link
 * goes. The modal is mounted globally (``PaywallProvider`` in
 * ``main.tsx``), so a plan cap hit while a CUSTOMER is configuring
 * their part pops the same dialog the operator sees — and the operator
 * copy names the internal resource key, the operator's plan tier and
 * ``/admin/plans``. None of that is the customer's business, and on the
 * public storefront the visitor is not even signed in.
 */
function isOperatorSurface(pathname: string): boolean {
  return pathname.split("/").filter(Boolean)[1] === "admin";
}

/**
 * Resolve the upgrade link target from the active route.
 *
 * Tenant admin shell (``/{lang}/admin/*``) → ``/{lang}/admin/plan`` —
 * the admin's own billing/plan page (where the upgrade CTA actually
 * lives). Anything else (customer-facing pages, signup, dev preview) →
 * ``/{lang}/cenik``, the public pricing landing. Without this, the
 * modal sent admins to the marketing site, which has no upgrade
 * action wired to the active tenant.
 */
function resolveUpgradeHref(pathname: string, fallbackLang: string): string {
  const langSegment = langOf(pathname, fallbackLang);
  if (isOperatorSurface(pathname)) {
    return `/${langSegment}/admin/plan`;
  }
  return `/${langSegment}/cenik`;
}

export function PaywallModal({
  context,
  onClose,
  upgradeHref,
}: {
  context: PaywallContext;
  onClose: () => void;
  /**
   * Optional explicit upgrade destination. When omitted, derived from
   * the active route — tenant admin shell → ``/admin/plan``, anywhere
   * else → public ``/cenik`` landing.
   */
  upgradeHref?: string;
}): JSX.Element {
  const { t, i18n } = useTranslation(["admin", "common"]);
  // window.location, NOT useLocation(): PaywallProvider mounts this
  // modal as a sibling of RouterProvider (main.tsx), i.e. OUTSIDE the
  // router context — a router hook here throws an invariant and the
  // root ErrorBoundary swallows the whole app. The pathname is only
  // read once at render time for a plain <a href>, so the non-reactive
  // global is equivalent.
  const resolvedHref =
    upgradeHref ?? resolveUpgradeHref(window.location.pathname, i18n.language);
  // An explicit ``upgradeHref`` is only ever passed from admin screens,
  // so it counts as the operator surface even if the pathname does not
  // (dev preview, a modal opened from a route that is not under /admin).
  const forOperator =
    upgradeHref !== undefined || isOperatorSurface(window.location.pathname);

  // Názvy zdrojů mají jeden zdroj (common:planLimits.resource.*) —
  // stránka plánu i upozornění u seznamů čtou tytéž (KAN-1665).
  const resourceLabel =
    planLimitResourceLabel(context.resource ?? "") ??
    t("admin:paywall.resourceGeneric");

  const title = forOperator
    ? context.kind === "entitlement"
      ? t("admin:paywall.entitlementTitle", { key: context.entitlementKey })
      : t("admin:paywall.countLimitTitle", { resource: resourceLabel })
    : context.kind === "entitlement"
      ? t("common:paywall.customerUnavailableTitle")
      : t("common:paywall.customerLimitTitle");

  const body = forOperator
    ? context.kind === "entitlement"
      ? t("admin:paywall.entitlementBody", { key: context.entitlementKey })
      : t("admin:paywall.countLimitBody", {
          current: context.current,
          limit: context.limit,
        })
    : context.kind === "entitlement"
      ? t("common:paywall.customerUnavailableBody")
      : // Bez čísla, když ho odpověď nenese — „nejvýš undefined variant"
        // je horší než věta bez čísla.
        typeof context.limit === "number"
        ? t("common:paywall.customerLimitBody", { limit: context.limit })
        : t("common:paywall.customerLimitBodyNoCount");

  return (
    <IngotModal
      title={title}
      onClose={onClose}
      closeLabel={t("paywall.close")}
      width={448}
      testId="paywall-modal"
    >
      <div>
        <p className="mb-4 text-sm text-ink-2">{body}</p>
        {/* Tarif provozovatele je obchodní údaj TENANTA, ne informace pro
            jeho zákazníka — na veřejné straně se nevypisuje ani tehdy,
            když ho odpověď nese. */}
        {forOperator && context.planTier && (
          <p className="mb-4 text-xs text-ink-3">
            {t("admin:paywall.currentPlan", { tier: context.planTier })}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border-strong px-3 py-1.5 text-sm"
          >
            {t("admin:paywall.close")}
          </button>
          {/* Odkaz na ceník / do administrace dává smysl jen tomu, kdo ten
              plán platí. Zákazníkovi nabízí upgrade cizího předplatného. */}
          {forOperator && (
            <a
              href={resolvedHref}
              className="rounded bg-ink px-3 py-1.5 text-sm font-medium text-bg"
              data-testid="paywall-upgrade"
            >
              {t("admin:paywall.upgrade")}
            </a>
          )}
        </div>
      </div>
    </IngotModal>
  );
}
